import { Suspense, useCallback, useMemo, useState } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { previewSource } from "@/lib/frame-preview-source";

import { normalizeDocFontSize } from "./doc-font-size";
import { frameStaticExcerpt } from "@/components/shapes/shared/frame-static-excerpt";
import { useFrameFontSize } from "@/components/shapes/shared/use-frame-font-size";
import { useFrameSurface } from "@/components/shapes/shared/use-frame-surface";
import { useFrameCommentAnchors } from "@/components/shapes/shared/use-frame-comment-anchors";
import { useFrameRenderer } from "@/components/shapes/shared/frame-renderer-context";
import { useOpenedFrame } from "@/components/shapes/shared/use-opened-frame";
import { docCoverImage } from "./doc-card-lines";
import { LazyDocEditor } from "./doc-editor.lazy";
import {
  FounderCardRender,
  ResearchSummaryRender,
} from "./doc-onboarding-renders";
import type { DocOpenedLead } from "./doc-opened-lead";
import { DocReadOnlyFallback } from "./doc-readonly-fallback";

/**
 * The Doc's text column (`--doc-pad-*`, read by `doc-editor.css`, the lead
 * and the excerpt): the card's margins in an opened window or a Pinned tile
 * (`--open-pad-*`), the canvas-era margins elsewhere.
 */
const DOC_PADDING =
  "[--doc-pad-bottom:var(--open-pad-block-end,20px)] [--doc-pad-end:var(--open-pad-inline,22px)]";

/**
 * While the Doc chunk loads: the card's plain-text excerpt. It starts where the editor's text starts
 * (`--doc-pad-*`, set by the Doc frame), so nothing shifts when the editor
 * arrives.
 */
export function DocLoadingExcerpt({ data }: { data: FrameData }) {
  const source = previewSource(data);
  const excerpt = useMemo(
    () => frameStaticExcerpt(data.frameType, source),
    [data.frameType, source],
  );
  return (
    <p
      className="min-h-0 flex-1 overflow-hidden whitespace-pre-line pb-[var(--doc-pad-bottom,10px)] pe-[var(--doc-pad-end,22px)] ps-[var(--doc-pad-start,22px)] pt-[var(--doc-pad-top,10px)] text-appkit-body text-muted"
      data-testid="doc-frame-loading"
    >
      {excerpt}
    </p>
  );
}

/** An opened Doc starts as its card does (`DocCardPreview`): cover, then title. */
function useDocOpenedLead(data: FrameData): DocOpenedLead | null {
  const opened = useOpenedFrame();
  const title = opened ? frameDisplayTitle(data) : "";
  const cover = opened
    ? docCoverImage(data.frameType, previewSource(data))
    : null;
  const coverUrl = cover?.url;
  const coverAlt = cover?.alt ?? "";
  return useMemo(
    () =>
      opened
        ? {
            title,
            cover: coverUrl ? { url: coverUrl, alt: coverAlt } : null,
          }
        : null,
    [opened, title, coverUrl, coverAlt],
  );
}

/**
 * A Doc: one BlockNote document on the frame's glass surface. Every text
 * frame renders here, whatever its stored format; legacy content converts in
 * memory and is saved as V4 only once the person edits it. A frame with an
 * onboarding `metadata.display_mode` shows that render instead.
 */
export function DocFrame(props: FrameProps) {
  const displayMode = props.data.metadata?.display_mode;
  if (displayMode === "founder_card") {
    return <FounderCardRender frame={props.data} />;
  }
  if (displayMode === "research_summary") {
    return <ResearchSummaryRender frame={props.data} />;
  }
  return <DocEditorFrame {...props} />;
}

function DocEditorFrame({ id, data, selected }: FrameProps) {
  const frameSurface = useFrameSurface(id);
  const handleFontSizeStep = useFrameFontSize(id, data.metadata?.font_size);
  const content = data.content ?? {};
  // Content that cannot convert falls back to read-only text, kept for this
  // mount.
  const [readOnly, setReadOnly] = useState(false);
  const handleConversionFailed = useCallback(() => setReadOnly(true), []);
  const frameTitle = data.title || "Untitled";
  const lead = useDocOpenedLead(data);
  const { focused, maximized } = useFrameRenderer().useChromeState(id);
  const editable = data.status !== "streaming" && !readOnly;
  const { commentAnchors, handleAnchorBadgeClick } = useFrameCommentAnchors(
    id,
    frameTitle,
  );

  return (
    <div
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden",
        DOC_PADDING,
        // Off an opened window, a focused Doc clears its hover chrome.
        focused
          ? "[--doc-pad-top:var(--open-pad-block-start,20px)]"
          : "[--doc-pad-top:var(--open-pad-block-start,10px)]",
        // An editable Doc leaves room for BlockNote's side menu (+ and the
        // drag handle that opens Turn into) at the start of its text column.
        editable
          ? "[--doc-pad-start:max(54px,var(--open-pad-inline,0px))]"
          : "[--doc-pad-start:var(--open-pad-inline,22px)]",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="doc-frame"
      data-frame-status={data.status}
      {...frameSurface.attributes}
    >
      {readOnly ? (
        <DocReadOnlyFallback frameType={data.frameType} content={content} />
      ) : (
        <Suspense fallback={<DocLoadingExcerpt data={data} />}>
          <LazyDocEditor
            frameId={id}
            frameTitle={frameTitle}
            frameType={data.frameType}
            content={content}
            status={data.status}
            selected={Boolean(selected)}
            fontSize={normalizeDocFontSize(data.metadata?.font_size)}
            onFontSizeStep={handleFontSizeStep}
            toolbarBelowChrome={maximized}
            lead={lead}
            onConversionFailed={handleConversionFailed}
            highlightedTexts={
              data.metadata?.highlightedTexts as string[] | undefined
            }
            commentAnchors={commentAnchors}
            onAnchorBadgeClick={handleAnchorBadgeClick}
          />
        </Suspense>
      )}
    </div>
  );
}
